import { useEffect, useState } from "react";
import type { DriverFollowStatus } from "../core/follow";
import { report } from "./report";

const POLL_MS = 15_000;
const REQUEST_TIMEOUT_MS = 10_000;

export interface FollowStatusResult {
	status: DriverFollowStatus | null;
	error: string | null;
}

/** A visible window onto the rider diagnostics the follow notifications already consume. */
export function useFollowStatus(enabled: boolean, deviceId: string): FollowStatusResult {
	const [result, setResult] = useState<FollowStatusResult>({ status: null, error: null });
	useEffect(() => {
		let disposed = false;
		let active: AbortController | null = null;
		let timeout: number | undefined;
		let failed = false;
		setResult({ status: null, error: null });
		if (!enabled) {
			return;
		}
		const load = async (reason: string) => {
			if (active && reason === "poll") return;
			active?.abort();
			window.clearTimeout(timeout);
			const controller = new AbortController();
			active = controller;
			const startedAtMs = Date.now();
			let httpStatus: number | null = null;
			timeout = window.setTimeout(() => {
				controller.abort();
				if (active === controller) active = null;
				failed = true;
				setResult(previous => ({ ...previous, error: "Journey update timed out. Retrying automatically." }));
				report("follow-status", { reason, outcome: "timeout", durationMs: Date.now() - startedAtMs, httpStatus, visibility: document.visibilityState });
			}, REQUEST_TIMEOUT_MS);
			try {
				const response = await fetch(`/api/follow/status?deviceId=${encodeURIComponent(deviceId)}`, { cache: "no-store", signal: controller.signal });
				httpStatus = response.status;
				if (!response.ok) throw new Error(`HTTP ${response.status}`);
				const body = (await response.json()) as { status: DriverFollowStatus | null };
				if (disposed || controller.signal.aborted || active !== controller) return;
				const next = body.status ?? null;
				setResult({ status: next, error: null });
				if (failed || reason !== "poll") {
					report("follow-status", { reason, outcome: "loaded", durationMs: Date.now() - startedAtMs, httpStatus, phase: next?.phase, direction: next?.direction, updatedAtMs: next?.updatedAtMs, visibility: document.visibilityState });
				}
				failed = false;
			} catch (cause) {
				if (disposed || controller.signal.aborted || active !== controller) return;
				failed = true;
				setResult(previous => ({ ...previous, error: "Journey update unavailable. Retrying automatically." }));
				report("follow-status", { reason, outcome: "failed", durationMs: Date.now() - startedAtMs, httpStatus, errorKind: cause instanceof Error ? cause.name : "unknown", visibility: document.visibilityState });
			} finally {
				if (active === controller) {
					window.clearTimeout(timeout);
					active = null;
				}
			}
		};
		void load("mount");
		const timer = window.setInterval(() => {
			if (document.visibilityState === "visible") {
				void load("poll");
			}
		}, POLL_MS);
		const onVisible = () => {
			if (document.visibilityState === "visible") {
				void load("visible");
			}
		};
		const onResume = (event: Event) => { void load(event.type); };
		document.addEventListener("visibilitychange", onVisible);
		window.addEventListener("focus", onResume);
		window.addEventListener("pageshow", onResume);
		window.addEventListener("online", onResume);
		return () => {
			disposed = true;
			active?.abort();
			window.clearTimeout(timeout);
			window.clearInterval(timer);
			document.removeEventListener("visibilitychange", onVisible);
			window.removeEventListener("focus", onResume);
			window.removeEventListener("pageshow", onResume);
			window.removeEventListener("online", onResume);
		};
	}, [deviceId, enabled]);

	return result;
}